import { useMemo, useState, CSSProperties, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { notificationApi } from 'hooks/api/notification'
import { SecurityInsightType } from 'types/insight'

const styles: Record<string, CSSProperties> = {
  container: {
    color: '#333',
    padding: '24px 20px 40px',
    margin: '0 auto',
    maxWidth: 480,
    fontSize: 16,
    lineHeight: 1.6,
    boxSizing: 'border-box',
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    textAlign: 'center',
    margin: '0 0 24px',
  },
  days: {
    textAlign: 'center',
    fontSize: 18,
    margin: '0 0 20px',
  },
  daysNum: {
    fontWeight: 700,
    fontSize: 24,
  },
  question: {
    textAlign: 'center',
    fontSize: 18,
    margin: '0 0 28px',
  },
  emphasis: { fontWeight: 600 },
  btnWrap: { display: 'flex', justifyContent: 'center', marginBottom: 20 },
  btnPrimary: {
    width: '90%',
    maxWidth: 360,
    height: 60,
    background: '#1976d2',
    color: '#fff',
    fontSize: 20,
    fontWeight: 700,
    border: 'none',
    borderRadius: 12,
    boxShadow: '0 2px 6px rgba(0,0,0,.15)',
    cursor: 'pointer',
    transition: 'opacity .2s ease, filter .2s ease',
  },
  btnSecondary: {
    width: '90%',
    maxWidth: 360,
    height: 60,
    background: '#e0e0e0',
    color: '#333',
    fontSize: 16,
    fontWeight: 600,
    border: 'none',
    borderRadius: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,.1)',
    cursor: 'pointer',
    transition: 'opacity .2s ease, filter .2s ease',
  },
  btnDisabled: {
    opacity: 0.5,
    filter: 'grayscale(100%)',
    cursor: 'default',
    boxShadow: 'none',
    pointerEvents: 'none',
  },
  doneMsg: {
    textAlign: 'center',
    fontSize: 14,
    marginTop: 8,
  },
  notes: { fontSize: 13, lineHeight: 1.5 },
}

export default function MoreWorkPage() {
  const { t } = useTranslation('page-insight-more-work')
  const [done, setDone] = useState(false)
  const params = new URLSearchParams(window.location.search)
  const notificationId = params.get('notificationId') ?? undefined

  const workedDays = useMemo(() => {
    const v = params.get('workedDays')
    if (!v) return undefined
    const num = Number(v)
    return Number.isNaN(num) ? undefined : num
  }, [])

  useEffect(() => {
    if (!notificationId) return
    notificationApi()
      .findInsightById(notificationId)
      .then((res) => {
        const data = res.data.data
        if (data && data.moreWork !== undefined && data.moreWork !== null) {
          setDone(true)
        }
      })
  }, [notificationId])

  const handleClick = (moreWork: boolean) => {
    if (!notificationId || done) return
    notificationApi()
      .upsertInsightData(notificationId, {
        data: { moreWork },
        insightType: SecurityInsightType.MoreWorkSimple,
      })
      .then(() => setDone(true))
  }

  const btnStyle = (variant: 'primary' | 'secondary'): CSSProperties => {
    const base = variant === 'primary' ? styles.btnPrimary : styles.btnSecondary
    return done ? { ...base, ...styles.btnDisabled } : base
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>
        {t('title_line1')}
        <br />
        {t('title_line2')}
      </h1>

      {typeof workedDays === 'number' && (
        <p style={styles.days}>
          <Trans
            i18nKey="worked_days"
            t={t}
            values={{ count: workedDays }}
            components={{ days: <span style={styles.daysNum} /> }}
          />
        </p>
      )}

      <p style={styles.question}>
        <Trans
          i18nKey="question"
          t={t}
          components={{
            emphasis: <span style={styles.emphasis} />,
            br: <br />,
          }}
        />
      </p>

      <div style={styles.btnWrap}>
        <button
          disabled={done}
          aria-disabled={done}
          style={btnStyle('primary')}
          onClick={() => handleClick(true)}
        >
          {t('can_work_more')}
        </button>
      </div>

      <div style={styles.btnWrap}>
        <button
          disabled={done}
          aria-disabled={done}
          style={btnStyle('secondary')}
          onClick={() => handleClick(false)}
        >
          {t('current_is_fine')}
        </button>
      </div>

      {done && <p style={styles.doneMsg}>{t('thank_you')}</p>}

      <div style={styles.notes}>
        <p>{t('note')}</p>
      </div>
    </div>
  )
}
